import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { CoverageEnum } from "@/lib/enum/liability.enum";
import { TripTypeEnum } from "@/lib/enum/trip-type.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IKsrJobDetailViewmodel } from "@/lib/interfaces/ksr/ksr-job-detail-viewmodel.interface";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { handleError } from "@/lib/utility/handleError";
import clearanceService from "@/services/mrfiktiv/services/clearance.service";
import sharedContentService from "@/services/mrfiktiv/services/sharedContentService";
import companyService from "@/services/mrfiktiv/services/companyService";
import partnerService from "@/services/mrfiktiv/services/partnerService";
import personService from "@/services/mrfiktiv/services/person.service";
import rentalService from "@/services/mrfiktiv/services/rental.service";
import repairEstimateService from "@/services/mrfiktiv/services/repair-estimate.service";
import reportService from "@/services/mrfiktiv/services/reportService";
import {
  MrfiktivAddressWithGeoLogGen,
  MrfiktivAttorneyDetailsGen,
  MrfiktivBasePartnerViewModelGen,
  MrfiktivBrokerDetailsGen,
  MrfiktivCreateActivityLogDtoGen,
  MrfiktivCreateInsuranceDtoGen,
  MrfiktivCreateLeasingDetailsDtoGen,
  MrfiktivCreatePoliceDetailsDtoGen,
  MrfiktivCreateReportWitnessDtoGen,
  MrfiktivCustomFieldValueGen,
  MrfiktivDaWebhookViewModelGen,
  MrfiktivDocumentViewModelGen,
  MrfiktivHandoverDetailViewModelGen,
  MrfiktivImageEditDataDtoGen,
  MrfiktivPartialAddressWithGeoViewModelGen,
  MrfiktivReportBodyGen,
  MrfiktivReportViewModelGen,
  MrfiktivServiceCompanyGen,
  MrfiktivStateHistoryGen,
  MrfiktivUpdateReportDtoGen,
  MrfiktivUpdateReportImagesDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { DamageAreaEnum } from "@/store/enum/damage-area.enum";
import { DamageTypeEnum } from "@/store/enum/damage-type.enum";
import { ProgressStatusEnum } from "@/store/enum/partner/progress.status.enum";
import { ReportType } from "@/store/enum/reportType";
import { FleetAggregationModule, IRefSuggestion } from "@/store/modules/fleet-aggregation.store";
import Vue from "vue";
import { AccidentDetails, IAccidentDetails } from "./accident-details.entity";
import { ActivityLog } from "./activity-log.entity";
import { Address, AddressWithGeo, IAddress } from "./address.entity";
import { BaseCollection, IBaseCollection } from "./base-collection.entity";
import { ByAt, IByAt } from "./by-at.entity";
import { Clearance, IClearance } from "./clearance.entity";
import { Company, CompanyBase, ICompany } from "./company.entity";
import { IImage, Image } from "./image.entity";
import { IInvoiceAggregation, InvoiceAggregation } from "./invoice-aggregation.entity";
import { IInvoiceBalance, InvoiceBalance } from "./invoice-balance.entity";
import { ILeasingDetails, LeasingDetails } from "./leasing.entity";
import { PageFilterElement } from "./page-filter-element.entity";
import { IPerson, Person } from "./person.entity";
import { IPoliceDetails, PoliceDetails } from "./police.entity";
import { IPreExistingDamageDetails, PreExistingDamageDetails } from "./pre-existing-damage.entity";
import { IRegistration, Registration } from "./registration.entity";
import { RegistrationResultsEntity } from "./registrationResultsEntity";
import { IRental, Rental } from "./rental.entity";
import { IRepairEstimate, RepairEstimate } from "./repair-estimate.entity";
import { IRepairDetails, RepairDetails } from "./repair.entity";
import { IShortUser, ShortUser } from "./short-user.entity";
import { ITimestampDocument, TimestampDocument } from "./timestamp.entity";
import { ITowingDetails, TowingDetails } from "./towing.entity";
import { Contact, IContact } from "./user-contact.entity";
import { IVehicle, Vehicle } from "./vehicle.entity";
import { IWitness, Witness } from "./witness.entity";

/**
 * import access layers last to avoid build problems
 */
import { CompanyServiceEnum } from "@/lib/enum/company-service.enum";
import { ReportRoleEnum } from "@/lib/enum/ReportRole.enum";
import { TotalLossType } from "@/lib/enum/total-loss-type.enum";
import { HttpException } from "@/lib/exceptions/http";
import { IsCustomViewable } from "@/lib/interfaces/is-custom-viewable.interface";
import { log } from "@/lib/utility/log";
import { $t } from "@/lib/utility/t";
import aiService from "@/services/mrfiktiv/services/aiService";
import appraisalService from "@/services/mrfiktiv/services/appraisal.service";
import { reportStateMap } from "@/store/mapper/report-icon.map";
import { AppraisalDataAccessLayer } from "@/store/modules/access-layers/appraisal.access-layer";
import { ClearanceDataAccessLayer } from "@/store/modules/access-layers/clearance.access-layer";
import { CompanyDataAccessLayer } from "@/store/modules/access-layers/company.access-layer";
import { PersonDataAccessLayer } from "@/store/modules/access-layers/person.access-layer";
import { RentalDataAccessLayer } from "@/store/modules/access-layers/rental.access-layer";
import { RepairEstimateDataAccessLayer } from "@/store/modules/access-layers/repair-estimate.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { SharedContentDataAccessLayer } from "@/store/modules/access-layers/shared-content.access-layer";
import { VehicleAccessLayer } from "@/store/modules/access-layers/vehicle.access-layer";
import { UserModule } from "@/store/modules/me-user.store";
import { ReportGroupModule } from "@/store/modules/report-group.store";
import { Appraisal, IAppraisal } from "./appraisal.entity";
import { ISharedContent, SharedContent } from "./shared-content.entity";
import { CustomFieldValue } from "./custom-field-value.entity";
import { Fleet, IFleet } from "./fleet.entity";
import { IReportGroup, ReportGroup } from "./report-group.entity";
import { ReportInsurance } from "./report-insurance.entity";
import { IReportPosition, ReportPosition } from "./report-position.entity";

@IsFilterable
class ReportBase implements MrfiktivReportViewModelGen, IsCustomViewable {
  /**
   * @inheritdoc
   */
  partnerId: string;

  /**
   * @inheritdoc
   */
  claimNumber: string;

  /**
   * @inheritdoc
   */
  title?: string;

  /**
   * @inheritdoc
   */
  status?: string;

  /**
   * @inheritdoc
   */
  statusHistory?: MrfiktivStateHistoryGen[] | undefined;

  /**
   * @inheritdoc
   */
  company?: string;

  /**
   * @inheritdoc
   */
  customerName: string;

  /**
   * @inheritdoc
   */
  firstName?: string;

  /**
   * @inheritdoc
   */
  lastName?: string;

  /**
   * @inheritdoc
   */
  isCompany?: boolean;

  /**
   * @inheritdoc
   */
  companyName?: string;

  /**
   * @inheritdoc
   */

  isTaxDeductible?: boolean;

  /**
   * @inheritdoc
   */

  taxnumber?: string;

  /**
   * @inheritdoc
   */
  @FilterConfig({
    type: ReportInsurance
  })
  insurances?: MrfiktivCreateInsuranceDtoGen[];

  /**
   * @inheritdoc
   */
  externalId?: string;

  /**
   * @inheritdoc
   */
  numberplate: string;

  /**
   * @inheritdoc
   */
  customerContact: IContact;

  /**
   * @inheritdoc
   */
  customerAddress: IAddress;

  /**
   * @inheritdoc
   */
  message: string;

  /**
   * @inheritdoc
   */
  registrationResults: IRegistration;

  /**
   * @inheritdoc
   */
  timestamps: ITimestampDocument;

  /**
   * @inheritdoc
   */
  timestamp: ITimestampDocument;

  /**
   * @inheritdoc
   */
  datePreference: string;

  /**
   * @inheritdoc
   */
  images: IBaseCollection;

  /**
   * @inheritdoc
   */
  attachment: IImage[];

  /**
   * @inheritdoc
   */
  attachmentCount: number;

  /**
   * @inheritdoc
   */
  damage: DamageAreaEnum[];

  /**
   * @inheritdoc
   */
  marketingOptIn: boolean;

  /**
   * @inheritdoc
   */
  progressStatus: ProgressStatusEnum;

  /**
   * @inheritdoc
   */
  reportType?: ReportType;

  /**
   * @inheritdoc
   */
  damageType?: DamageTypeEnum;

  /**
   * @inheritdoc
   */
  coverageType?: CoverageTypeEnum;

  /**
   * @inheritdoc
   */
  coverage?: CoverageEnum;

  /**
   * @inheritdoc
   */
  witness?: IWitness[];

  /**
   * @inheritdoc
   */
  accidentDetails?: IAccidentDetails;

  /**
   * @inheritdoc
   */
  policeDetails?: IPoliceDetails;

  /**
   * @inheritdoc
   */
  leasing?: ILeasingDetails;

  /**
   * @inheritdoc
   */
  broker?: MrfiktivBrokerDetailsGen;

  /**
   * @inheritdoc
   */
  attorney?: MrfiktivAttorneyDetailsGen;

  /**
   * @inheritdoc
   */
  repair?: IRepairDetails;

  /**
   * @inheritdoc
   */
  towing?: ITowingDetails[];

  /**
   * @inheritdoc
   */
  preExistingDamageDetails?: IPreExistingDamageDetails;

  /**
   * @inheritdoc
   */
  body?: MrfiktivReportBodyGen[];

  /**
   * @inheritdoc
   */
  summary?: string;

  /**
   * @inheritdoc
   */
  summaryId?: string;

  /**
   * @inheritdoc
   */
  workshopCompanyId?: string;

  /**
   * @inheritdoc
   */
  appraiserCompanyId?: string;

  /**
   * @inheritdoc
   */
  insuranceCompanyId?: string;

  /**
   * @inheritdoc
   */
  leasingCompanyId?: string;

  /**
   * @inheritdoc
   */
  towingCompanyId?: string;

  /**
   * @inheritdoc
   */
  attorneyCompanyId?: string;

  /**
   * @inheritdoc
   */
  rentalCompanyId?: string;

  /**
   * @inheritdoc
   */
  damageBrokerCompanyId?: string;

  /**
   * @inheritdoc
   */
  insuranceBrokerCompanyId?: string;

  /**
   * @inheritdoc
   */
  serviceCompanies?: MrfiktivServiceCompanyGen[] | null;

  /**
   * @inheritdoc
   */
  companyId?: string;

  /**
   * @inheritdoc
   */
  fleetId?: string;

  /**
   * @inheritdoc
   */
  personIds?: string[];

  /**
   * @inheritdoc
   */
  personId?: string;

  /**
   * @inheritdoc
   */
  _id: string;

  /**
   * @inheritdoc
   */
  id: string;

  /**
   * @inheritdoc
   */
  vehicleId?: string;

  /**
   * @inheritdoc
   */
  userId?: string;

  /**
   * @inheritdoc
   */
  user?: IShortUser;

  /**
   * @inheritdoc
   */
  assignees: string[];

  /**
   * @inheritdoc
   */
  assigneesDetails: IShortUser[];

  /**
   * @inheritdoc
   */
  forwarded?: IByAt;

  /**
   * @inheritdoc
   */
  distributor?: MrfiktivBasePartnerViewModelGen | undefined;

  /**
   * @inheritdoc
   */

  position?: IReportPosition;

  /**
   * @inheritdoc
   */
  handover?: MrfiktivHandoverDetailViewModelGen;

  /**
   * @inheritdoc
   */
  isWrapped?: boolean | undefined;

  /**
   *
   */
  isWrapDamaged?: boolean | undefined;

  damageLocationDescription?: string | undefined;

  /**
   * @inheritdoc
   */
  liabilitySharePercent?: number | null;

  /**
   * @inheritdoc
   */
  isDeductions?: boolean | null;

  /**
   * @inheritdoc
   */
  isDepreciationCredit?: boolean | null;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.appraisal.totalLoss",
    config: {
      items: Object.values(TotalLossType).map((status) => ({
        value: status,
        text: `enums.TotalLossType.${status}`
      })),
      itemValue: "value"
    }
  })
  totalLoss?: TotalLossType | null;

  criticality?: "minor" | "moderate" | "major" | undefined;
  isFixed?: boolean | undefined;

  countryCode?: CountryCodeEnum;
  language: LanguageCodeEnum;

  isDrivable?: boolean | undefined;
  isRoadworthy?: boolean | undefined;
  isUnderInfluence?: boolean | undefined;
  isValidDriversLicense?: boolean | undefined;

  whatsAppContactOptIn?: boolean | undefined;

  tripType?: TripTypeEnum | undefined;

  location?: MrfiktivPartialAddressWithGeoViewModelGen | undefined;

  locationHistory?: MrfiktivAddressWithGeoLogGen[];

  costCenter?: string | null | undefined;

  mileage?: number | undefined;

  /**
   * Aggregated invoice data for this report
   */
  @FilterConfig({
    type: InvoiceAggregation
  })
  invoiceAggregation?: IInvoiceAggregation;

  /**
   * Aggregated inbound invoice data for this report
   */
  @FilterConfig({
    type: InvoiceAggregation
  })
  invoiceInboundAggregation?: IInvoiceAggregation;

  /**
   * Aggregated outbound invoice data for this report
   */
  @FilterConfig({
    type: InvoiceAggregation
  })
  invoiceOutboundAggregation?: IInvoiceAggregation;

  /**
   * Revenue ("Einnahmen"), expense ("Ausgaben") and balance ("Saldo") of the invoices of this report by currency
   */
  @FilterConfig({
    type: InvoiceBalance
  })
  invoiceBalance?: IInvoiceBalance;

  /**
   * Aggregated invoice data for this report
   */
  @FilterConfig({
    type: InvoiceAggregation
  })
  invoiceSelfContributionAggregation?: IInvoiceAggregation;

  workShopCompany: ICompany | null = null;
  appraiserCompany: ICompany | null = null;
  insuranceCompany: ICompany | null = null;
  leasingCompany: ICompany | null = null;
  towingCompany: ICompany | null = null;
  attorneyCompany: ICompany | null = null;
  rentalCompany: ICompany | null = null;
  damageBrokerCompany: ICompany | null = null;
  insuranceBrokerCompany: ICompany | null = null;
  companyCompany: ICompany | null = null;
  repairEstimates: IRepairEstimate[] = [];
  appraisals: IAppraisal[] = [];
  rentals: IRental[] = [];
  vehicle: IVehicle | null = null;
  fleet: IFleet | null = null;
  clearances: IClearance[] = [];

  /**
   * The driver, populated from personId
   */
  person: IPerson | null = null;

  /**
   * Populated from personIds
   */
  persons: IPerson[] = [];

  group: IReportGroup | null = null;

  isWorkshopCompanyLoading = false;
  isAppraiserCompanyLoading = false;
  isInsuranceCompanyLoading = false;
  isLeasingCompanyLoading = false;
  isTowingCompanyLoading = false;
  isAttorneyCompanyLoading = false;
  isRentalCompanyLoading = false;
  isInsuranceBrokerCompanyLoading = false;
  isDamageBrokerCompanyLoading = false;
  isCompanyCompanyLoading = false;

  isPersonsLoading = false;
  isCompaniesLoading = false;

  isAppraisalLoading = false;
  isRepairEstimateLoading = false;
  isRentalsLoading = false;
  isVehicleLoading = false;
  isFleetLoading = false;
  isClearancesLoading = false;
  isReportGroupLoading = false;

  values?: MrfiktivCustomFieldValueGen[] = [];

  documents?: MrfiktivDocumentViewModelGen[];

  groupId?: string;

  loading = false;

  get fleetCompanyIds() {
    return this.fleet?.companyIds;
  }

  /**
   * Whether a vehicle is assigned to this report.
   */
  get hasVehicle() {
    return Boolean(this.vehicleId);
  }

  get accidentDate() {
    return this.accidentDetails?.date || this.timestamps?.created || new Date();
  }

  get projectId(): Partial<string | undefined> {
    return this.groupId;
  }

  set projectId(id: string | undefined) {
    this.groupId = id;
  }

  get titleReadable() {
    if (this.title) {
      return this.title;
    }

    return $t("contract.noTitle");
  }

  get statusMessage() {
    const length = this.statusHistory?.length;
    if (!this.statusHistory || !length) {
      return "";
    }

    return this.statusHistory[length - 1]?.message || "";
  }

  constructor(report?: Partial<MrfiktivReportViewModelGen>) {
    this._id = report?.id ?? report?._id ?? "";
    this.id = this._id;
    this.partnerId = report?.partnerId ?? "";
    this.claimNumber = report?.claimNumber ?? "";
    this.title = report?.title ?? "";
    this.status = report?.status;
    this.statusHistory = report?.statusHistory ?? [];
    this.company = report?.company ?? "";
    this.customerName = report?.customerName ?? "";
    this.firstName = report?.firstName;
    this.lastName = report?.lastName;
    this.isCompany = report?.isCompany;
    this.companyName = report?.companyName;
    this.isTaxDeductible = report?.isTaxDeductible;
    this.taxnumber = report?.taxnumber;
    this.insurances = report?.insurances ?? [];
    this.externalId = report?.externalId;
    this.numberplate = report?.numberplate ?? "";
    this.language = (report?.language as LanguageCodeEnum) ?? LanguageCodeEnum.DE;
    this.countryCode = (report?.countryCode as CountryCodeEnum) ?? CountryCodeEnum.germany;
    this.customerContact = (
      report?.customerContact ? new Contact(report?.customerContact, this.countryCode) : undefined
    ) as IContact;
    this.customerAddress = (report?.customerAddress ? new Address(report?.customerAddress) : undefined) as IAddress;
    this.message = report?.message ?? "";
    this.registrationResults = new Registration(report?.registrationResults);
    this.datePreference = report?.datePreference ?? "";
    this.images = new BaseCollection(report?.images);
    this.attachment = (report?.attachment ?? []).map((r) => new Image(r));
    this.attachmentCount = report?.attachmentCount ?? 0;
    this.damage = (report?.damage ?? []).map((r) => r as DamageAreaEnum);
    this.damageLocationDescription = report?.damageLocationDescription;
    this.totalLoss = report?.totalLoss as TotalLossType;
    this.marketingOptIn = report?.marketingOptIn ?? false;
    this.progressStatus = (report?.progressStatus as ProgressStatusEnum) ?? ProgressStatusEnum.DRAFT;
    this.reportType = report?.reportType as ReportType;
    this.witness = (report?.witness ?? []).map((r) => new Witness(r));
    this.accidentDetails = report?.accidentDetails ? new AccidentDetails(report?.accidentDetails) : undefined;
    this.policeDetails = report?.policeDetails ? new PoliceDetails(report?.policeDetails) : undefined;
    this.leasing = report?.leasing ? new LeasingDetails(report?.leasing) : undefined;
    this.broker = report?.broker;
    this.attorney = report?.attorney;
    this.repair = report?.repair ? new RepairDetails(report.repair) : undefined;
    this.towing = report?.towing?.map((t) => new TowingDetails(t));
    this.preExistingDamageDetails = report?.preExistingDamageDetails
      ? new PreExistingDamageDetails(report?.preExistingDamageDetails)
      : undefined;
    this.body = (report?.body ?? []).map((r) => r);

    if (report?.vehicle) {
      this.vehicle = new Vehicle(report.vehicle);
    }
    this.vehicleId = report?.vehicleId;

    this.userId = report?.userId;
    this.user = report?.user ? new ShortUser(report?.user) : undefined;
    this.assignees = report?.assignees ?? [];
    this.assigneesDetails = (report?.assigneesDetails ?? []).map((r) => new ShortUser(r));
    this.forwarded = report?.forwarded ? new ByAt(report?.forwarded) : undefined;
    this.distributor = report?.distributor;
    this.summary = report?.summary;
    this.summaryId = report?.summaryId;

    this.serviceCompanies = report?.serviceCompanies ?? [];
    this.companyId = report?.companyId;
    this.personIds = report?.personIds;
    this.personId = report?.personId;

    if (report?.fleetId && report.fleet) {
      this.fleet = new Fleet(report.fleet);
    }
    this.fleetId = report?.fleetId;

    if (report?.workShopCompany) {
      this.workShopCompany = new CompanyBase(report.workShopCompany);
    }
    this.workshopCompanyId = report?.workshopCompanyId;

    if (report?.appraiserCompany) {
      this.appraiserCompany = new CompanyBase(report.appraiserCompany);
    }
    this.appraiserCompanyId = report?.appraiserCompanyId;

    if (report?.insuranceCompany) {
      this.insuranceCompany = new CompanyBase(report.insuranceCompany);
    }
    this.insuranceCompanyId = report?.insuranceCompanyId;

    if (report?.leasingCompany) {
      this.leasingCompany = new CompanyBase(report.leasingCompany);
    }
    this.leasingCompanyId = report?.leasingCompanyId;

    if (report?.towingCompany) {
      this.towingCompany = new CompanyBase(report.towingCompany);
    }
    this.towingCompanyId = report?.towingCompanyId;

    if (report?.attorneyCompany) {
      this.attorneyCompany = new CompanyBase(report.attorneyCompany);
    }
    this.attorneyCompanyId = report?.attorneyCompanyId;

    if (report?.rentalCompany) {
      this.rentalCompany = new CompanyBase(report.rentalCompany);
    }
    this.rentalCompanyId = report?.rentalCompanyId;

    if (report?.damageBrokerCompany) {
      this.damageBrokerCompany = new CompanyBase(report.damageBrokerCompany);
    }
    this.damageBrokerCompanyId = report?.damageBrokerCompanyId;

    if (report?.insuranceBrokerCompany) {
      this.insuranceBrokerCompany = new CompanyBase(report.insuranceBrokerCompany);
    }
    this.insuranceBrokerCompanyId = report?.insuranceBrokerCompanyId;

    this.liabilitySharePercent = report?.liabilitySharePercent;
    this.isDeductions = report?.isDeductions;
    this.isDepreciationCredit = report?.isDepreciationCredit;

    this.timestamps = new TimestampDocument(report?.timestamps);
    this.timestamp = new TimestampDocument(report?.timestamp);
    this.position = report?.position ? new ReportPosition(report.position) : undefined;
    this.handover = report?.handover;
    this.isFixed = report?.isFixed;
    this.coverage = report?.coverage as CoverageEnum;

    this.isWrapDamaged = report?.isWrapDamaged;
    this.isWrapped = report?.isWrapped;

    this.isDrivable = report?.isDrivable;
    this.isRoadworthy = report?.isRoadworthy;
    this.isUnderInfluence = report?.isUnderInfluence;
    this.isValidDriversLicense = report?.isValidDriversLicense;
    this.whatsAppContactOptIn = report?.whatsAppContactOptIn;

    this.tripType = report?.tripType as TripTypeEnum | undefined;
    this.damageType = report?.damageType as DamageTypeEnum | DamageTypeEnum.UNKNOWN;
    this.coverageType = report?.coverageType as CoverageTypeEnum | CoverageTypeEnum.OTHER;

    this.location = report?.location ? new AddressWithGeo(report?.location) : undefined;
    this.locationHistory = report?.locationHistory;
    this.invoiceAggregation = report?.invoiceAggregation
      ? new InvoiceAggregation(report.invoiceAggregation)
      : undefined;
    this.invoiceInboundAggregation = report?.invoiceInboundAggregation
      ? new InvoiceAggregation(report.invoiceInboundAggregation)
      : undefined;
    this.invoiceOutboundAggregation = report?.invoiceOutboundAggregation
      ? new InvoiceAggregation(report.invoiceOutboundAggregation)
      : undefined;
    this.invoiceBalance = report?.invoiceBalance ? new InvoiceBalance(report.invoiceBalance) : undefined;
    this.invoiceSelfContributionAggregation = report?.invoiceSelfContributionAggregation
      ? new InvoiceAggregation(report.invoiceSelfContributionAggregation)
      : undefined;

    this.values = CustomFieldValue.buildCustomFieldValues(report?.values || []) as MrfiktivCustomFieldValueGen[];
    this.groupId = report?.groupId;
    this.costCenter = report?.costCenter;
    this.documents = report?.documents;
    this.mileage = report?.mileage;
    this.syncGroupStatusCustomFieldValue();
  }

  /**
   * The group's status column is rendered from `values` (via `configuration.status.customField.id`),
   * but `status`/`statusHistory` are written directly by several backend paths without touching `values`.
   * Synthesize the missing entry from `status` so already-drifted reports still render correctly.
   */
  private syncGroupStatusCustomFieldValue() {
    if (!this.groupId || !this.status) {
      return;
    }

    const group = this.group ?? ReportGroupModule.maps.id.get(this.groupId)[0];
    const statusCustomFieldId = group?.configuration.status?.customField?.id;

    if (statusCustomFieldId && !this.values?.some((v) => v.id === statusCustomFieldId)) {
      this.values = [...(this.values ?? []), new CustomFieldValue({ id: statusCustomFieldId, value: this.status })];
    }
  }

  private map(report?: Partial<MrfiktivReportViewModelGen>) {
    const id = report?.id ?? report?._id;
    if (id) {
      this._id = id;
      this.id = id;
    }
    this.partnerId = report?.partnerId ?? "";
    this.claimNumber = report?.claimNumber ?? "";
    this.title = report?.title ?? "";
    this.company = report?.company ?? "";
    this.customerName = report?.customerName ?? "";
    this.firstName = report?.firstName;
    this.lastName = report?.lastName;
    this.companyName = report?.companyName;
    this.taxnumber = report?.taxnumber;
    this.insurances = report?.insurances;
    this.externalId = report?.externalId;
    this.numberplate = report?.numberplate ?? "";
    this.customerContact = (
      report?.customerContact ? new Contact(report?.customerContact, this.countryCode) : undefined
    ) as IContact;
    this.customerAddress = (report?.customerAddress ? new Address(report?.customerAddress) : undefined) as IAddress;
    this.message = report?.message ?? "";
    this.registrationResults = new Registration(report?.registrationResults);
    this.datePreference = report?.datePreference ?? "";
    this.images = new BaseCollection(report?.images);
    this.attachment = (report?.attachment ?? []).map((r) => new Image(r));
    this.attachmentCount = report?.attachmentCount ?? 0;
    this.damage = (report?.damage ?? []).map((r) => r as DamageAreaEnum);
    this.damageLocationDescription = report?.damageLocationDescription;
    this.totalLoss = report?.totalLoss as TotalLossType;
    this.marketingOptIn = report?.marketingOptIn ?? false;
    this.progressStatus = report?.progressStatus as ProgressStatusEnum;
    this.status = report?.status;
    this.statusHistory = report?.statusHistory ?? [];
    this.reportType = report?.reportType as ReportType;
    this.witness = (report?.witness ?? []).map((r) => new Witness(r));
    this.accidentDetails = report?.accidentDetails ? new AccidentDetails(report?.accidentDetails) : undefined;
    this.policeDetails = report?.policeDetails ? new PoliceDetails(report?.policeDetails) : undefined;
    this.leasing = report?.leasing ? new LeasingDetails(report?.leasing) : undefined;
    this.broker = report?.broker;
    this.attorney = report?.attorney;
    this.repair = report?.repair ? new RepairDetails(report.repair) : undefined;
    this.towing = report?.towing?.map((t) => new TowingDetails(t));
    this.preExistingDamageDetails = report?.preExistingDamageDetails
      ? new PreExistingDamageDetails(report?.preExistingDamageDetails)
      : undefined;
    this.body = (report?.body ?? []).map((r) => r);
    this.vehicleId = report?.vehicleId;
    this.userId = report?.userId;
    this.user = new ShortUser(report?.user);
    this.assignees = report?.assignees ?? [];
    this.assigneesDetails = (report?.assigneesDetails ?? []).map((r) => new ShortUser(r));
    this.forwarded = new ByAt(report?.forwarded);
    this.timestamps = new TimestampDocument(report?.timestamps);
    this.timestamp = new TimestampDocument(report?.timestamp);
    this.isCompany = report?.isCompany;
    this.isTaxDeductible = report?.isTaxDeductible;
    this.summary = report?.summary;
    this.summaryId = report?.summaryId;

    this.liabilitySharePercent = report?.liabilitySharePercent;
    this.isDeductions = report?.isDeductions;
    this.isDepreciationCredit = report?.isDepreciationCredit;

    this.serviceCompanies = report?.serviceCompanies ?? [];

    if (report?.workShopCompany) {
      this.workShopCompany = new CompanyBase(report.workShopCompany);
    }
    this.workshopCompanyId = report?.workshopCompanyId;

    if (report?.appraiserCompany) {
      this.appraiserCompany = new CompanyBase(report.appraiserCompany);
    }
    this.appraiserCompanyId = report?.appraiserCompanyId;

    if (report?.insuranceCompany) {
      this.insuranceCompany = new CompanyBase(report.insuranceCompany);
    }
    this.insuranceCompanyId = report?.insuranceCompanyId;

    if (report?.leasingCompany) {
      this.leasingCompany = new CompanyBase(report.leasingCompany);
    }
    this.leasingCompanyId = report?.leasingCompanyId;

    if (report?.towingCompany) {
      this.towingCompany = new CompanyBase(report.towingCompany);
    }
    this.towingCompanyId = report?.towingCompanyId;

    if (report?.attorneyCompany) {
      this.attorneyCompany = new CompanyBase(report.attorneyCompany);
    }
    this.attorneyCompanyId = report?.attorneyCompanyId;

    if (report?.rentalCompany) {
      this.rentalCompany = new CompanyBase(report.rentalCompany);
    }
    this.rentalCompanyId = report?.rentalCompanyId;

    if (report?.damageBrokerCompany) {
      this.damageBrokerCompany = new CompanyBase(report.damageBrokerCompany);
    }
    this.damageBrokerCompanyId = report?.damageBrokerCompanyId;

    if (report?.insuranceBrokerCompany) {
      this.insuranceBrokerCompany = new CompanyBase(report.insuranceBrokerCompany);
    }
    this.insuranceBrokerCompanyId = report?.insuranceBrokerCompanyId;

    this.companyId = report?.companyId;
    this.personIds = report?.personIds;
    this.personId = report?.personId;

    if (report?.fleetId && report.fleet) {
      this.fleet = new Fleet(report.fleet);
    }
    this.fleetId = report?.fleetId;

    this.position = report?.position ? new ReportPosition(report.position) : undefined;
    this.handover = report?.handover;
    this.isFixed = report?.isFixed;
    this.coverage = report?.coverage as CoverageEnum;

    this.isWrapDamaged = report?.isWrapDamaged;
    this.isWrapped = report?.isWrapped;

    this.isDrivable = report?.isDrivable;
    this.isRoadworthy = report?.isRoadworthy;
    this.isUnderInfluence = report?.isUnderInfluence;
    this.isValidDriversLicense = report?.isValidDriversLicense;
    this.whatsAppContactOptIn = report?.whatsAppContactOptIn;

    this.damageType = report?.damageType as DamageTypeEnum | DamageTypeEnum.UNKNOWN;
    this.coverageType = report?.coverageType as CoverageTypeEnum | CoverageTypeEnum.OTHER;

    this.location = report?.location ? new AddressWithGeo(report?.location) : undefined;
    this.locationHistory = report?.locationHistory;

    this.tripType = report?.tripType as TripTypeEnum | undefined;
    this.invoiceAggregation = report?.invoiceAggregation
      ? new InvoiceAggregation(report.invoiceAggregation)
      : undefined;
    this.invoiceInboundAggregation = report?.invoiceInboundAggregation
      ? new InvoiceAggregation(report.invoiceInboundAggregation)
      : undefined;

    this.invoiceOutboundAggregation = report?.invoiceOutboundAggregation
      ? new InvoiceAggregation(report.invoiceOutboundAggregation)
      : undefined;
    this.invoiceBalance = report?.invoiceBalance ? new InvoiceBalance(report.invoiceBalance) : undefined;
    this.invoiceSelfContributionAggregation = report?.invoiceSelfContributionAggregation
      ? new InvoiceAggregation(report.invoiceSelfContributionAggregation)
      : undefined;
    this.values = CustomFieldValue.buildCustomFieldValues(report?.values || []) as MrfiktivCustomFieldValueGen[];
    this.groupId = report?.groupId;
    this.costCenter = report?.costCenter;
    this.documents = report?.documents;
    this.mileage = report?.mileage;
    this.syncGroupStatusCustomFieldValue();
  }

  color(progressStatus: ProgressStatusEnum) {
    return reportStateMap.get(progressStatus)?.color || "blue-grey lighten-4";
  }

  /**
   * Maps company IDs to their respective company entities
   * @param companies Array of companies to map to the entity properties
   */
  mapCompanies(companies: ICompany[]) {
    if (!companies || companies.length === 0) {
      return;
    }

    // Create a map of company IDs to company objects for quick lookup
    const companyMap = new Map<string, ICompany>();
    companies.forEach((company) => {
      if (company.id) {
        companyMap.set(company.id, company);
      }
    });

    // Map company IDs to their respective entity properties
    if (this.workshopCompanyId && companyMap.has(this.workshopCompanyId)) {
      this.workShopCompany = companyMap.get(this.workshopCompanyId) || null;
    }

    if (this.appraiserCompanyId && companyMap.has(this.appraiserCompanyId)) {
      this.appraiserCompany = companyMap.get(this.appraiserCompanyId) || null;
    }

    if (this.insuranceCompanyId && companyMap.has(this.insuranceCompanyId)) {
      this.insuranceCompany = companyMap.get(this.insuranceCompanyId) || null;
    }

    if (this.leasingCompanyId && companyMap.has(this.leasingCompanyId)) {
      this.leasingCompany = companyMap.get(this.leasingCompanyId) || null;
    }

    if (this.towingCompanyId && companyMap.has(this.towingCompanyId)) {
      this.towingCompany = companyMap.get(this.towingCompanyId) || null;
    }

    if (this.attorneyCompanyId && companyMap.has(this.attorneyCompanyId)) {
      this.attorneyCompany = companyMap.get(this.attorneyCompanyId) || null;
    }

    if (this.rentalCompanyId && companyMap.has(this.rentalCompanyId)) {
      this.rentalCompany = companyMap.get(this.rentalCompanyId) || null;
    }

    if (this.insuranceBrokerCompanyId && companyMap.has(this.insuranceBrokerCompanyId)) {
      this.insuranceBrokerCompany = companyMap.get(this.insuranceBrokerCompanyId) || null;
    }
    if (this.damageBrokerCompanyId && companyMap.has(this.damageBrokerCompanyId)) {
      this.damageBrokerCompany = companyMap.get(this.damageBrokerCompanyId) || null;
    }

    if (this.companyId && companyMap.has(this.companyId)) {
      this.companyCompany = companyMap.get(this.companyId) || null;
    }
  }

  async fetch(silent = false) {
    this.loading = true;
    try {
      const report = await partnerService.getReportById(this.partnerId, this.id);

      this.map(report);

      ReportDataAccessLayer.set(this);
    } catch (e) {
      if (silent) {
        throw e;
      }

      handleError(e);
    } finally {
      this.loading = false;
    }

    return this;
  }

  async fetchSharedContent(): Promise<ISharedContent[]> {
    const res = await sharedContentService.getSharedContentPaginated({
      partnerId: this.partnerId,
      filter: [
        new PageFilterElement({
          key: "reportId",
          operation: PageFilterOperationEnum.EQUAL,
          value: this.id
        }).dtoPagination
      ]
    });

    const mapped = (res.data ?? []).map((sc) => {
      const entity = new SharedContent(sc);
      SharedContentDataAccessLayer.set(entity);
      return entity;
    });

    return mapped;
  }

  async fetchCompanies(fetchShared = false) {
    this.isCompaniesLoading = true;
    try {
      const companiesVM = await companyService.getMany(this.partnerId, {
        ids: [
          this.attorneyCompanyId,
          this.workshopCompanyId,
          this.appraiserCompanyId,
          this.insuranceCompanyId,
          this.leasingCompanyId,
          this.towingCompanyId,
          this.rentalCompanyId,
          this.damageBrokerCompanyId,
          this.insuranceBrokerCompanyId,
          this.companyId
        ].filter((id): id is string => id != null)
      });
      const companies = companiesVM.map((c) => new Company(c));
      companies.forEach((c) => CompanyDataAccessLayer.set(c));
      if (fetchShared) companies.forEach((c) => c.fetchSharedContent(this.id));
      this.mapCompanies(companies);
    } catch (e) {
      handleError(e);
    } finally {
      this.isCompaniesLoading = false;
    }

    return this;
  }

  async fetchPersons(fetchShared = false) {
    if (!this.personIds) {
      this.personIds = [];
    }

    this.isPersonsLoading = true;

    const ids = this.personIds.filter((id): id is string => id != null);

    if (this.personId) {
      ids.push(this.personId);
    }

    if (ids.length === 0) {
      this.isPersonsLoading = false;
      return this;
    }

    try {
      const persons = await personService.getMany(this.partnerId, {
        ids
      });
      /**
       * set active person as driver
       */
      const driver = persons.find((p) => p.id === this.personId);
      if (driver) {
        this.person = new Person(driver);
        PersonDataAccessLayer.set(this.person);
        if (fetchShared) this.person.fetchSharedContent(this.id);
      } else {
        this.person = null;
      }

      /**
       * set other persons and remove active driver from person array
       */
      this.persons = persons.map((p) => new Person(p)).filter((p) => p.id !== this.personId);

      this.persons.forEach((p) => PersonDataAccessLayer.set(p));
      if (fetchShared) this.persons.forEach((p) => p.fetchSharedContent(this.id));
    } catch (e) {
      handleError(e);
    } finally {
      this.isPersonsLoading = false;
    }

    return this;
  }

  async updateProgressStatus(progressStatus: ProgressStatusEnum) {
    Vue.$log.info("updating progress status to " + progressStatus);
    try {
      const res = await partnerService.updateProgressStatus(this.partnerId, this.id, { progressStatus });

      this.map(res);
    } catch (error) {
      Vue.$log.error(error);
    }

    Vue.$log.info("new progress status " + this.progressStatus);

    ReportDataAccessLayer.set(this);

    await this.addProgressStatusActivityLog();

    FleetAggregationModule.replaceReport(this);
  }

  /**
   *
   * @param status
   */
  async deleteCustomProgressStatusActivityLog(status: string) {
    try {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: {
          refType: BackendResourceEnum.REPORT,
          refId: this.id
        },
        actionType: ActionEnum.DELETE,
        activity: ActivityTypeEnum.UPDATE_PROGRESS_STATUS,
        comment: status
      };
      await new ActivityLog({ partnerId: this.partnerId, ...data }).create();
    } catch (error) {
      Vue.$log.error(error);
    }
  }

  /**
   *
   * @param status
   */
  async updateCustomProgressStatusActivityLog(status: string) {
    try {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: {
          refType: BackendResourceEnum.REPORT,
          refId: this.id
        },
        actionType: ActionEnum.UPDATE,
        activity: ActivityTypeEnum.UPDATE_PROGRESS_STATUS,
        comment: status
      };
      await new ActivityLog({ partnerId: this.partnerId, ...data }).create();
    } catch (error) {
      Vue.$log.error(error);
    }
  }

  async addProgressStatusActivityLog() {
    try {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: {
          refType: BackendResourceEnum.REPORT,
          refId: this.id
        },
        actionType: ActionEnum.UPDATE,
        activity: ActivityTypeEnum.UPDATE_PROGRESS_STATUS,
        comment: this.progressStatus
      };
      await new ActivityLog({ partnerId: this.partnerId, ...data }).create();
    } catch (error) {
      Vue.$log.error(error);
      Vue.$log.error("Error Saving Activity");
    }
  }

  async updateIsFixed(isFixed: boolean) {
    Vue.$log.info("updating isFixed to " + isFixed);
    try {
      const res = await this.updatePartial({ isFixed });

      this.map(res);
    } catch (error) {
      Vue.$log.error(error);
    }

    Vue.$log.info("new isFixed " + this.isFixed);

    ReportDataAccessLayer.set(this);

    try {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: {
          refType: BackendResourceEnum.REPORT,
          refId: this.id
        },
        actionType: ActionEnum.UPDATE,
        activity: ActivityTypeEnum.UPDATE_IS_FIXED_STATUS,
        comment: this.isFixed?.toString()
      };
      await new ActivityLog({ partnerId: this.partnerId, ...data }).create();
    } catch (error) {
      Vue.$log.error(error);
    }

    FleetAggregationModule.replaceReport(this);
  }

  async updatePartial(updateDto: MrfiktivUpdateReportDtoGen) {
    if (updateDto.values) {
      updateDto.values = CustomFieldValue.buildCustomFieldValuesDto(updateDto.values);
    }
    const updated = await reportService.updateReport(this.partnerId, this.id, updateDto);

    this.map(updated);

    ReportDataAccessLayer.set(this);

    FleetAggregationModule.replaceReport(this);

    return this;
  }

  async updateReportImages(updateDto: MrfiktivUpdateReportImagesDtoGen) {
    const updated = await reportService.updateReportImages(this.partnerId, this.id, updateDto);

    this.map(updated);

    ReportDataAccessLayer.set(this);

    return updated;
  }

  async editReportImages(imageId: string, data: MrfiktivImageEditDataDtoGen) {
    const updated = await reportService.editReportImage(this.partnerId, this.id, imageId, data);

    this.map(updated);

    ReportDataAccessLayer.set(this);

    return updated;
  }

  async create() {
    try {
      const created = await reportService.createAsPartner(this.partnerId, {
        accidentDetails: this.accidentDetails,
        assignees: this.assignees,
        body: this.body,
        companyName: this.companyName,
        customerAddress:
          this.customerAddress?.city ||
          this.customerAddress?.state ||
          this.customerAddress?.state ||
          this.customerAddress?.street
            ? this.customerAddress
            : undefined,
        customerContact: this.customerContact.dto ? this.customerContact.dto : undefined,
        customerName: this.customerName ? this.customerName : undefined,
        damage: this.damage,
        datePreference: this.datePreference ? this.datePreference : undefined,
        externalId: this.externalId,
        leasing: this.leasing,
        marketingOptIn: this.marketingOptIn,
        message: this.message,
        numberplate: this.numberplate,
        policeDetails: this.policeDetails,
        preExistingDamageDetails: this.preExistingDamageDetails,
        registrationResults: this.registrationResults,
        reportType: this.reportType,
        vehicleId: this.vehicleId,
        images: this.images.imageCollectionDto,
        title: this.title,
        isDrivable: this.isDrivable,
        isWrapped: this.isWrapped,
        isWrapDamaged: this.isWrapDamaged,
        isRoadworthy: this.isRoadworthy,
        isUnderInfluence: this.isUnderInfluence,
        isValidDriversLicense: this.isValidDriversLicense,
        whatsAppContactOptIn: this.whatsAppContactOptIn,
        liabilitySharePercent: this.liabilitySharePercent,
        isDeductions: this.isDeductions,
        isDepreciationCredit: this.isDepreciationCredit,
        values: CustomFieldValue.buildCustomFieldValuesDto(this.values || []),
        groupId: this.groupId
      });

      this.map(created);

      ReportDataAccessLayer.set(this);
      FleetAggregationModule.parseReports([{ ...created, id: created._id }]);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  async addRegistrationResults(registrationResult: RegistrationResultsEntity) {
    try {
      const response = await partnerService.addRegistrationDetailsForReport(
        this.partnerId,
        this.id,
        registrationResult
      );

      this.map(response);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  async addVehicle(vehicle: IVehicle) {
    const dto: MrfiktivUpdateReportDtoGen = { vehicleId: vehicle.id };

    dto.costCenter = vehicle?.costCenter;

    if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.COMPANY, this.partnerId)) {
      const insuranceContract = vehicle?.activeInsuranceContract();

      if (insuranceContract?.companyId) {
        const company = await new Company({ id: insuranceContract.companyId, partnerId: this.partnerId })
          .fetch()
          .catch((e) => handleError(e));

        if (company) {
          insuranceContract.company = company;

          dto.insurances = [
            {
              name: insuranceContract.company.companyName || "",
              number: ""
            }
          ];

          dto.insuranceCompanyId = insuranceContract.companyId;
        }
      }

      const leasingContract = vehicle?.activeLeasingContract();
      if (leasingContract?.companyId) {
        const company = await new Company({ id: leasingContract.companyId, partnerId: this.partnerId })
          .fetch()
          .catch((e) => handleError(e));

        if (company) {
          leasingContract.company = company;

          dto.leasing = {
            isLeasing: true,
            company: leasingContract.company.companyName || "",
            claimNumber: ""
          };

          dto.leasingCompanyId = leasingContract.companyId;
        }
      }

      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.FLEET, this.partnerId)) {
        await vehicle.fetchFleet().catch((e) => handleError(e));
        await vehicle?.fleet?.fetchNextRootCompany().catch((e) => handleError(e));
        await vehicle?.fleet?.fetchCompanies().catch((e) => handleError(e));

        dto.companyId = vehicle.fleet?.companyId;
        dto.companyName = vehicle?.fleet?.company?.companyName;
        dto.insuranceBrokerCompanyId = vehicle?.fleet?.getCompanyByServiceEnum(CompanyServiceEnum.INSURANCE_BROKER)?.id;
        dto.attorneyCompanyId = vehicle?.fleet?.getCompanyByServiceEnum(CompanyServiceEnum.LAWYER)?.id;

        if (!dto.insuranceCompanyId) {
          dto.insuranceCompanyId = vehicle?.fleet?.getCompanyByServiceEnum(CompanyServiceEnum.INSURANCE)?.id;
        }

        if (!dto.leasingCompanyId) {
          dto.leasingCompanyId = vehicle.fleet?.getCompanyByServiceEnum(CompanyServiceEnum.LEASING)?.id;
        }
      }
    }

    const r = await this.updatePartial(dto);
    this.vehicle = vehicle;

    const data: MrfiktivCreateActivityLogDtoGen = {
      source: {
        refId: this.id,
        refType: BackendResourceEnum.REPORT
      },
      target: [
        {
          refType: BackendResourceEnum.VEHICLE,
          refId: vehicle.id
        }
      ],
      actionType: ActionEnum.UPDATE,
      activity: ActivityTypeEnum.UPDATE_REPORT
    };

    await new ActivityLog({
      partnerId: this.partnerId,
      ...data
    }).create();

    return r;
  }

  static createFromKsrJob(job: IKsrJobDetailViewmodel, partnerId: string) {
    const report = new Report();

    report.partnerId = partnerId;

    const value = job.value as any;
    report.numberplate = job.numberPlate;
    report.registrationResults.numberplate = value.vehicle.registrationDetails.registrationPlate;
    report.registrationResults.identificationnumber = value.vehicle.registrationDetails.registrationDocumentNumber;
    report.registrationResults.manufacturerName = value.vehicle.typeDetails?.manufacturer;
    report.registrationResults.manufacturerDescription = value.vehicle.typeDetails?.model;
    report.registrationResults.manufacturerType = value.vehicle.typeDetails?.productionSeries;
    report.registrationResults.manufacturerNameCode = value.vehicle.typeDetails?.manufacturerCode;

    // this is the owner (type: 1) but not the customer. Customer might be based on customerPartyId (includes mail and phone)
    const foundOwner = value.involvements?.involvedParties?.find((party: { type: number }) => party.type === 1);
    if (foundOwner) {
      report.firstName = foundOwner?.personalData.firstName;
      report.lastName = foundOwner?.personalData.lastName;
      report.customerName = `${foundOwner?.personalData.firstName} ${foundOwner?.personalData.lastName}`;
      report.customerContact = new Contact({
        email: foundOwner?.personalData.eMailAddresses[0],
        phone: foundOwner?.personalData.phone.mobile
      });
      report.customerAddress = {
        street: foundOwner?.postalAddress.street,
        city: foundOwner?.postalAddress.city,
        zip: foundOwner?.postalAddress.zipCode,
        state: ""
      };
    }

    const foundInsurance = value.involvements?.involvedParties?.find((party: { type: number }) => party.type === 7);
    if (foundInsurance) {
      report.insurances = [
        {
          name: foundInsurance.personalData.lastName
        }
      ];
    }

    const foundBroker = value.involvements?.involvedParties?.find((party: { type: number }) => party.type === 7);
    if (foundBroker) {
      report.broker = {
        name: foundBroker.personalData.lastName
      };
    }

    return report;
  }

  static createFromDaWebhook(webhook: MrfiktivDaWebhookViewModelGen, partnerId: string) {
    const report = new Report();

    report.partnerId = partnerId;
    report.externalId = `${webhook?.data?.dossier.id}`;

    if (webhook?.data?.dossier?.vehicle) {
      report.title = webhook.data.dossier.vehicle.license_plate;
      report.numberplate = webhook.data.dossier.vehicle.license_plate ?? "";

      report.registrationResults.identificationnumber = webhook.data?.dossier?.vehicle?.vehicle_identification_number;
      report.registrationResults.numberplate = webhook?.data?.dossier?.vehicle?.license_plate;

      report.registrationResults.manufacturerName = webhook.data.dossier.vehicle?.brand;
      report.registrationResults.manufacturerDescription = webhook.data.dossier.vehicle?.model;

      if (webhook.data.dossier.vehicle?.first_registration_date) {
        const [y, m, d] = webhook.data.dossier.vehicle.first_registration_date.split("-");
        report.registrationResults.firstregistrationYear = y;
        report.registrationResults.firstregistrationMonth = m;
        report.registrationResults.firstregistrationDay = d;
      }

      if (webhook.data.dossier.vehicle?.general_inspection_date) {
        const [y, m] = webhook.data.dossier.vehicle?.general_inspection_date.split("-");
        report.registrationResults.huMonth = m;
        report.registrationResults.huYear = y;
      }
    }

    if (webhook?.data?.dossier?.contact) {
      report.customerName = `${webhook?.data?.dossier?.contact?.first_name} ${webhook?.data?.dossier?.contact?.last_name}`;

      report.firstName = webhook.data.dossier.contact.first_name;
      report.lastName = webhook.data.dossier.contact.last_name;
      report.company = webhook.data.dossier.contact.company;

      report.customerContact = new Contact({
        email: webhook.data.dossier.contact.email ?? "",
        phone: webhook.data.dossier.contact.mobile_number ?? webhook.data.dossier.contact.phone_number ?? ""
      });

      report.customerAddress = {
        street: webhook.data.dossier.contact.street ?? "",
        city: webhook.data.dossier.contact.city ?? "",
        zip: webhook.data.dossier.contact.zip_code ?? "",
        state: ""
      };
    }

    report.insurances = webhook.insuranceDetails;
    report.broker = webhook.brokerDetails;

    return report;
  }

  async fetchWorkshopEntity(fetchShared = false) {
    if (!this.workshopCompanyId) {
      this.workShopCompany = null;

      return;
    }
    try {
      this.isWorkshopCompanyLoading = true;
      this.workShopCompany = await new Company({ id: this.workshopCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.workShopCompany.id) this.workShopCompany.fetchSharedContent(this.id);
    } finally {
      this.isWorkshopCompanyLoading = false;
    }
  }

  async fetchDamageBrokerEntity(fetchShared = false) {
    if (!this.damageBrokerCompanyId) {
      this.damageBrokerCompany = null;

      return;
    }
    try {
      this.isDamageBrokerCompanyLoading = true;
      this.damageBrokerCompany = await new Company({
        id: this.damageBrokerCompanyId,
        partnerId: this.partnerId
      }).fetch();
      if (fetchShared && this.damageBrokerCompany.id) this.damageBrokerCompany.fetchSharedContent(this.id);
    } finally {
      this.isDamageBrokerCompanyLoading = false;
    }
  }

  async fetchAppraiserEntity(fetchShared = false) {
    if (!this.appraiserCompanyId) {
      this.appraiserCompany = null;
      return;
    }
    try {
      this.isAppraiserCompanyLoading = true;
      this.appraiserCompany = await new Company({ id: this.appraiserCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.appraiserCompany.id) this.appraiserCompany.fetchSharedContent(this.id);
    } finally {
      this.isAppraiserCompanyLoading = false;
    }
  }

  async fetchInsuranceEntity(fetchShared = false) {
    if (!this.insuranceCompanyId) {
      this.insuranceCompany = null;
      return;
    }
    try {
      this.isInsuranceCompanyLoading = true;
      this.insuranceCompany = await new Company({ id: this.insuranceCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.insuranceCompany.id) this.insuranceCompany.fetchSharedContent(this.id);
    } finally {
      this.isInsuranceCompanyLoading = false;
    }
  }

  async fetchLeasingEntity(fetchShared = false) {
    if (!this.leasingCompanyId) {
      this.leasingCompany = null;
      return;
    }
    try {
      this.isLeasingCompanyLoading = true;
      this.leasingCompany = await new Company({ id: this.leasingCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.leasingCompany.id) this.leasingCompany.fetchSharedContent(this.id);
    } finally {
      this.isLeasingCompanyLoading = false;
    }
  }

  async fetchTowingEntity(fetchShared = false) {
    if (!this.towingCompanyId) {
      this.towingCompany = null;
      return;
    }
    try {
      this.isTowingCompanyLoading = true;
      this.towingCompany = await new Company({ id: this.towingCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.towingCompany.id) this.towingCompany.fetchSharedContent(this.id);
    } finally {
      this.isTowingCompanyLoading = false;
    }
  }

  async fetchAttorneyEntity(fetchShared = false) {
    if (!this.attorneyCompanyId) {
      this.attorneyCompany = null;
      return;
    }
    try {
      this.isAttorneyCompanyLoading = true;
      this.attorneyCompany = await new Company({ id: this.attorneyCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.attorneyCompany.id) this.attorneyCompany.fetchSharedContent(this.id);
    } finally {
      this.isAttorneyCompanyLoading = false;
    }
  }

  async fetchRentalCompany(fetchShared = false) {
    if (!this.rentalCompanyId) {
      this.rentalCompany = null;
      return;
    }
    try {
      this.isRentalCompanyLoading = true;
      this.rentalCompany = await new Company({ id: this.rentalCompanyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.rentalCompany.id) this.rentalCompany.fetchSharedContent(this.id);
    } finally {
      this.isRentalCompanyLoading = false;
    }
  }

  async fetchCompanyEntity(fetchShared = false) {
    if (!this.companyId) {
      this.companyCompany = null;
      return;
    }
    try {
      this.isCompanyCompanyLoading = true;
      this.companyCompany = await new Company({ id: this.companyId, partnerId: this.partnerId }).fetch();
      if (fetchShared && this.companyCompany.id) {
        this.companyCompany.fetchSharedContent(this.id);
      }
    } finally {
      this.isCompanyCompanyLoading = false;
    }
  }

  async fetchCostEstimates(fetchShared = false) {
    this.isRepairEstimateLoading = true;
    try {
      const costEstimates = await repairEstimateService.get({
        partnerId: this.partnerId,
        filter: [new PageFilterElement({ key: "reportId", value: this.id })]
      });
      this.repairEstimates = [];

      if (!costEstimates.data) {
        return;
      }

      for (const costEstimate of costEstimates.data) {
        const costEstimateEntity = new RepairEstimate(costEstimate);
        costEstimateEntity.fetchDocument();
        if (fetchShared) {
          costEstimateEntity.fetchSharedContent();
        }
        RepairEstimateDataAccessLayer.set(costEstimateEntity);
        this.repairEstimates.push(costEstimateEntity);
      }
    } finally {
      this.isRepairEstimateLoading = false;
    }
  }

  async fetchAppraisals(fetchShared = false) {
    this.isAppraisalLoading = true;
    try {
      const appraisals = await appraisalService.get({
        partnerId: this.partnerId,
        filter: [new PageFilterElement({ key: "reportId", value: this.id })]
      });
      this.appraisals = [];

      if (!appraisals.data) {
        return;
      }

      for (const appraisal of appraisals.data) {
        const appraisalEntity = new Appraisal(appraisal);
        appraisalEntity.fetchDocument();
        if (fetchShared) {
          appraisalEntity.fetchSharedContent();
        }
        AppraisalDataAccessLayer.set(appraisalEntity);
        this.appraisals.push(appraisalEntity);
      }
    } finally {
      this.isAppraisalLoading = false;
    }
  }

  async fetchRentals() {
    this.isRentalsLoading = true;
    try {
      const results = await rentalService.getAll({
        partnerId: this.partnerId,
        filter: [new PageFilterElement({ key: "reportId", value: this.id })]
      });
      if (!results.data) {
        return;
      }

      this.rentals = [];

      for (const r of results.data) {
        const rental = new Rental(r);
        RentalDataAccessLayer.set(rental);
        this.rentals.push(rental);
      }
    } finally {
      this.isRentalsLoading = false;
    }
  }

  async fetchVehicle() {
    if (!this.vehicleId) {
      this.vehicle = null;
      return;
    }

    this.isVehicleLoading = true;
    try {
      const vehicle =
        VehicleAccessLayer.get(this.vehicleId) ??
        (await new Vehicle({
          id: this.vehicleId,
          partnerId: this.partnerId
        }).fetch());

      this.vehicle = vehicle ?? null;
    } catch (error) {
      // If vehicle is not found just set it to null
      if (error instanceof HttpException && error.data?.statusCode === 404) {
        Vue.$log.warn(`Deleted vehicle ${this.vehicleId} set to undefined on report ${this.id}`);
        this.vehicleId = undefined;
        this.vehicle = null;

        return;
      }

      throw error;
    } finally {
      this.isVehicleLoading = false;
    }
  }

  async fetchFleet() {
    this.isFleetLoading = true;
    try {
      const fleetId = this.fleetId ?? this.vehicle?.groupId;

      if (fleetId) {
        const fleet = await new Fleet({
          id: fleetId,
          partnerId: this.partnerId
        }).fetch();

        this.fleet = fleet;
      }
    } finally {
      this.isFleetLoading = false;
    }
  }

  async fetchClearances(fetchShared = false) {
    this.isClearancesLoading = true;

    try {
      const clearances = await clearanceService.getAll({
        partnerId: this.partnerId,
        filter: [
          new PageFilterElement({
            key: "reportId",
            operation: PageFilterOperationEnum.EQUAL,
            value: this.id
          })
        ]
      });

      if (!clearances.data) {
        this.clearances = [];
        return;
      }

      const uniqueClearances = new Map<string, InstanceType<typeof Clearance>>();

      for (const clearance of clearances.data) {
        let clearanceEntity = ClearanceDataAccessLayer.get(clearance.id);
        if (!clearanceEntity) {
          clearanceEntity = new Clearance(clearance);

          ClearanceDataAccessLayer.set(clearanceEntity);
        }
        if (fetchShared) {
          await clearanceEntity.fetchSharedContent();
        }
        uniqueClearances.set(clearanceEntity.id, clearanceEntity);
      }

      this.clearances = Array.from(uniqueClearances.values());
    } finally {
      this.isClearancesLoading = false;
    }

    const clearanceFetches = this.clearances.map((clearance) => clearance.fetchClearanceApprovals());

    await Promise.all(clearanceFetches);
  }

  async fetchReportGroup() {
    if (!this.groupId) return;

    this.isReportGroupLoading = true;
    try {
      const groupId = this.groupId;

      if (groupId) {
        const group = await new ReportGroup({
          id: groupId,
          partnerId: this.partnerId
        }).fetch();

        this.group = group;
      }
    } finally {
      this.isReportGroupLoading = false;
    }
  }

  get suggestedRefs() {
    const suggestions: IRefSuggestion[] = [];

    suggestions.push({
      id: this.id,
      ref: this,
      refType: BackendResourceEnum.REPORT
    });

    if (this.vehicle) {
      suggestions.push({
        id: this.vehicle.id,
        ref: this.vehicle,
        refType: BackendResourceEnum.VEHICLE
      });
    }

    this.repairEstimates.forEach((repairEstimate) => {
      suggestions.push({
        id: repairEstimate.id,
        ref: repairEstimate,
        refType: BackendResourceEnum.REPAIR_ESTIMATE
      });
    });

    this.appraisals.forEach((appraisal) => {
      suggestions.push({
        id: appraisal.id,
        ref: appraisal,
        refType: BackendResourceEnum.APPRAISAL
      });
    });

    this.rentals.forEach((rental) => {
      suggestions.push({
        id: rental.id,
        ref: rental,
        refType: BackendResourceEnum.RENTAL
      });
    });

    this.clearances.forEach((clearance) => {
      suggestions.push({
        id: clearance.id,
        ref: clearance,
        refType: BackendResourceEnum.CLEARANCE
      });
    });

    return suggestions;
  }

  /**
   * Method to update the custom field defining the status of the group
   * @param status
   * @param historyMessage
   * @returns
   */
  async setProgressWithHistory(status: string, historyMessage?: string) {
    const customFieldStatusConfig = this.group?.configuration.status?.customField;

    let valuesToUpdate: MrfiktivCustomFieldValueGen[] | undefined = undefined;
    if (customFieldStatusConfig?.configuration?.values.find((v) => v.value === status)) {
      const existingValue: MrfiktivCustomFieldValueGen = this.values?.find(
        (c) => c.id === customFieldStatusConfig?.id
      ) ?? {
        id: customFieldStatusConfig?.id,
        value: ""
      };

      existingValue.value = status;

      valuesToUpdate = [...(this.values?.filter((v) => v.id !== existingValue.id) ?? []), existingValue];
    }

    await this.updatePartial({
      status: status,
      values: valuesToUpdate ? CustomFieldValue.buildCustomFieldValuesDto(valuesToUpdate) : undefined,
      statusHistory: [
        ...(this.statusHistory || []),
        {
          status: status,
          created: new Date().toISOString(),
          message: historyMessage
        }
      ]
    });

    await this.updateCustomProgressStatusActivityLog(status);
  }

  async update() {
    this.loading = true;

    const updateDto = Report.mapReportToUpdateDto(this, {
      isBroker: true,
      isInsurance: true,
      isLeasingDetails: true,
      isWitness: true,
      isPoliceDetails: true,
      isAccidentDetails: true,
      isContact: true,
      isImages: true,
      isReport: true,
      isPosition: true,
      isCustomFields: true,
      isHistory: true
    });

    // When the report is updated entirely we check if the statusHistory must be updated. this enabled synchronization of CustomView with statusHhistory
    if (this.groupId) {
      const group =
        this.group ??
        ReportGroupModule.maps.id.get(this.groupId)[0] ??
        (await this.fetchReportGroup().catch((e) => {
          log.error(e);
          return undefined;
        }));
      const groupStatusCustomField = group?.configuration.status?.customField;
      const statusCustomFieldValue = updateDto.values?.find((v) => v.id === groupStatusCustomField?.id)?.value;
      const statusCustomFieldValueAsString = Array.isArray(statusCustomFieldValue)
        ? statusCustomFieldValue[0]
        : statusCustomFieldValue;
      const valueHasChanged =
        this.statusHistory?.[this.statusHistory.length - 1]?.status !== statusCustomFieldValueAsString;

      if (statusCustomFieldValueAsString && valueHasChanged) {
        updateDto.status = statusCustomFieldValueAsString;
        updateDto.statusHistory?.push({
          created: new Date().toISOString(),
          status: statusCustomFieldValueAsString
        });
        if (UserModule.abilities.can(ActionEnum.CREATE, ResourceEnum.ACTIVITY, this.partnerId)) {
          await this.updateCustomProgressStatusActivityLog(statusCustomFieldValueAsString);
        }
      }
    }

    await this.updatePartial(updateDto);

    this.loading = false;

    return this;
  }

  static mapReportToUpdateDto(
    report: IReport,
    {
      isBroker,
      isInsurance,
      isLeasingDetails,
      isWitness,
      isPoliceDetails,
      isAccidentDetails,
      isContact,
      isImages,
      isReport,
      isPosition,
      isCustomFields,
      isHistory
    }: {
      isBroker?: boolean;
      isInsurance?: boolean;
      isLeasingDetails?: boolean;
      isWitness?: boolean;
      isPoliceDetails?: boolean;
      isAccidentDetails?: boolean;
      isContact?: boolean;
      isImages?: boolean;
      isReport?: boolean;
      isPosition?: boolean;
      isCustomFields?: boolean;
      isHistory?: boolean;
    }
  ): MrfiktivUpdateReportDtoGen {
    let mapReportToUpdateDto: MrfiktivUpdateReportDtoGen = {};

    mapReportToUpdateDto.vehicleId = report.vehicleId;

    if (report.message) {
      mapReportToUpdateDto = {
        ...mapReportToUpdateDto,
        message: report.message
      };
    }
    mapReportToUpdateDto = {
      ...mapReportToUpdateDto,
      customerName: report.customerName,
      firstName: report.firstName,
      lastName: report.lastName,
      isCompany: report.isCompany,
      companyName: report.companyName,
      isTaxDeductible: report.isTaxDeductible,
      taxnumber: report.taxnumber,
      externalId: report.externalId,
      numberplate: report.numberplate ?? "",
      message: report.message || "",
      title: report.title || report.numberplate || "",
      damage: (report.damage as any) || [],
      reportType: report.reportType,
      datePreference: report.datePreference,
      body: report.body,
      values: undefined
    };

    if (isHistory) {
      mapReportToUpdateDto.statusHistory = report.statusHistory ?? [];
    }

    if (isCustomFields) {
      mapReportToUpdateDto.values = report.values;
    }

    if ((isReport || isPosition) && report.position) {
      mapReportToUpdateDto.position = report.position.dto;
    }

    if (isImages) {
      mapReportToUpdateDto.images = {
        registrations: report.images.registrations.map((i) => i.id).filter((i) => i) as string[],
        cockpits: report.images.cockpits.map((i) => i.id).filter((i) => i) as string[],
        damages: report.images.damages.map((i) => i.id).filter((i) => i) as string[],
        damagesDetail: report.images.damagesDetail.map((i) => i.id).filter((i) => i) as string[],
        overviews: report.images.overviews.map((i) => i.id).filter((i) => i) as string[]
      };
    }

    if (isContact && report.customerContact) {
      mapReportToUpdateDto.customerContact = report.customerContact.dto;
    }
    if (isContact && report.customerAddress) {
      mapReportToUpdateDto.customerAddress = report.customerAddress;
    }

    mapReportToUpdateDto.companyId = report.companyId;

    mapReportToUpdateDto.workshopCompanyId = report.workshopCompanyId;
    mapReportToUpdateDto.appraiserCompanyId = report.appraiserCompanyId;
    mapReportToUpdateDto.insuranceCompanyId = report.insuranceCompanyId;
    mapReportToUpdateDto.leasingCompanyId = report.leasingCompanyId;
    mapReportToUpdateDto.towingCompanyId = report.towingCompanyId;
    mapReportToUpdateDto.attorneyCompanyId = report.attorneyCompanyId;
    mapReportToUpdateDto.rentalCompanyId = report.rentalCompanyId;
    mapReportToUpdateDto.personIds = report.personIds;
    mapReportToUpdateDto.serviceCompanies = report.serviceCompanies;
    mapReportToUpdateDto.mileage = report.mileage;

    if (isAccidentDetails && report.accidentDetails) {
      const accidentDetails = {
        address: {
          city: report.accidentDetails.address.city || "",
          geo: { lat: report.accidentDetails.address.geo.lat || 0, lng: report.accidentDetails.address.geo.lng || 0 },
          state: report.accidentDetails.address.state || "",
          street: report.accidentDetails.address.street || "",
          zip: report.accidentDetails.address.zip || "",
          countryCode: report.accidentDetails.address.countryCode || CountryCodeEnum.germany
        },
        comment: report.accidentDetails.comment || "",
        date: report.accidentDetails.date,
        role: report.accidentDetails.role || ReportRoleEnum.UNCLEAR
      };

      mapReportToUpdateDto = { ...mapReportToUpdateDto, accidentDetails };
    }
    if (isPoliceDetails && report.policeDetails) {
      const policeDetails: MrfiktivCreatePoliceDetailsDtoGen = {
        isRecorded: report.policeDetails.isRecorded,
        department: report.policeDetails.department || "",
        fileReference: report.policeDetails.fileReference || ""
      };

      mapReportToUpdateDto = { ...mapReportToUpdateDto, policeDetails };
    }

    if (isWitness) {
      const witness: MrfiktivCreateReportWitnessDtoGen[] = report.witness?.map((w) => w.dto) || [];

      mapReportToUpdateDto = { ...mapReportToUpdateDto, witness };
    }

    if (isLeasingDetails && report.leasing) {
      const leasing: MrfiktivCreateLeasingDetailsDtoGen = {
        isLeasing: report.leasing?.isLeasing || false,
        company: report.leasing?.company || "",
        claimNumber: report.leasing?.claimNumber || ""
      };

      mapReportToUpdateDto = { ...mapReportToUpdateDto, leasing };
    }

    if (isInsurance && report.insurances) {
      const insurances = report.insurances.map((i) => ({
        name: i.name || "",
        number: i.number || "",
        type: i.type || undefined
      }));

      mapReportToUpdateDto = {
        ...mapReportToUpdateDto,
        insurances
      };
    }

    if (isBroker && report.broker) {
      const broker = {
        name: report.broker.name || "",
        number: report.broker.number || ""
      };

      mapReportToUpdateDto = {
        ...mapReportToUpdateDto,
        broker
      };
    }

    mapReportToUpdateDto.preExistingDamageDetails = report.preExistingDamageDetails || new PreExistingDamageDetails();

    return mapReportToUpdateDto;
  }

  async generateFromMessageWithAi(messageId: string): Promise<{ isReportMessage: boolean; report?: IReport }> {
    const res = await aiService.aiGenerateReportFromMessage(this.partnerId, messageId);

    if (!res?.isReportMessage || !res?.report) {
      Vue.$log.error(new Error(`generateFromMessageWithAi ${messageId} ${res.reasoning}`));
      return {
        isReportMessage: false
      };
    }

    this.map(res.report);

    ReportDataAccessLayer.set(this);

    return { report: this, isReportMessage: false };
  }

  get refFilter() {
    if (!this.id) {
      return [];
    }

    return [
      new PageFilterElement({
        key: "refs.refType",
        operation: PageFilterOperationEnum.EQUAL,
        value: ResourceEnum.REPORT
      }),
      new PageFilterElement({
        key: "refs.refId",
        operation: PageFilterOperationEnum.EQUAL,
        value: this.id
      })
    ];
  }

  async delete() {
    await reportService.delete(this.partnerId, this.id);
    ReportDataAccessLayer.delete(this);
    await FleetAggregationModule.removeReport(this.id);
  }
}

type IReport = ReportBase;
const Report = Filter.createForClass(ReportBase);

export { IReport, Report };
